<script setup>
/**
 * One piece of approved (or refused, or paused, or waiting) wording, as WhatsApp holds it.
 *
 * `OutboundTemplatesCard`'s row, deliberately: the same name/body/facts order, the same
 * `dir="auto"` + `pre-wrap` + height-capped body (README §2 — an unbounded block of somebody
 * else's Arabic stretched a page to 2.6× the viewport), and the same reason for showing the
 * BODY at all rather than only the name: the name is Meta's (`eid_offer_v2_final`) and tells
 * the owner nothing about what her customer would read.
 *
 * NOTHING ON THIS ROW IS DEFAULTED — not the status, not the category, not the quality rating,
 * not the reason a template was refused. See `TemplateStatusChip` for why that is the point of
 * this screen and not defensive habit. A missing category says so in words; it never reads
 * "UTILITY" because UTILITY is the common case.
 *
 * ── WHAT THE ROW CARRIES AND WHAT THE PANEL CARRIES ─────────────────────────────────
 *
 * Meta's template object has fifteen fields. The row carries the four an owner SCANS by — the
 * name, WhatsApp's verdict (with its quality rating folded in, as WhatsApp Manager has it), the
 * message itself, and the handful of facts that fit on one line — and `TemplateFacts` carries
 * the rest behind one tap. At four hundred templates that division is the difference between a
 * screen and a wall of text.
 *
 * `rejected_reason` is on the ROW rather than only in the panel, because without it a refused row
 * is a dead end: the owner can see the wording was refused and has no way at all to learn why.
 */
import { computed, ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import TemplateFacts from './TemplateFacts.vue';
import TemplateQualityChip from './TemplateQualityChip.vue';
import TemplateStatusChip from './TemplateStatusChip.vue';
import {
  KNOWN_CATEGORIES,
  KNOWN_REASONS,
  canEdit,
  blockerPart,
  countBlanks,
  describeTtl,
  editBlockers,
} from '../helpers/template-facts.js';
import { useWasloI18n } from '../i18n.js';

const props = defineProps({
  /** The narrowed row from `api/templates.js`. */
  template: { type: Object, required: true },
  /** Which tier of Meta's field list answered: 'full' | 'core' | 'basic'. */
  fields: { type: String, default: 'basic' },
  managerUrl: { type: String, default: '' },
  busy: { type: String, default: '' },
  /** Passed straight through to the performance panel, which fetches on demand. */
  accountId: { type: Number, default: null },
  inboxId: { type: String, default: '' },
});

const emit = defineEmits(['edit', 'unpause', 'remove']);

const { t } = useWasloI18n();

const open = ref(false);

const blanks = computed(() => countBlanks(props.template));

/**
 * TRANSLATED WHEN WE HAVE A WORD FOR IT, and Meta's own word when we do not.
 *
 * It used to render the raw value, so an Arabic owner read `MARKETING` in Latin capitals on the
 * primary scan line of every row — while the filter above it and the facts panel below it both
 * showed the Arabic. Three renderings of one field on one screen, and the raw one was the one she
 * reads first. The key has existed since this screen shipped.
 *
 * Meta still returns a long legacy list on old templates (`SHIPPING_UPDATE`, `OTP`, …) and can add
 * to it, so an unknown value is shown as Meta wrote it rather than folded into the nearest one.
 */
const category = computed(() => {
  const raw = props.template.category;
  if (!raw) return t('WASLO.TEMPLATES.ROW.CATEGORY_UNTOLD');
  const folded = raw.toUpperCase();
  return KNOWN_CATEGORIES.includes(folded)
    ? t(`WASLO.TEMPLATES.CATEGORY.${folded}`)
    : raw;
});

const reason = computed(() => {
  const raw = props.template.rejectedReason;
  if (!raw) return '';
  const folded = raw.toUpperCase();
  // Meta keeps adding reasons, and its webhook enum is already larger than its field enum. One it
  // has just invented is shown as Meta wrote it rather than silently dropped — an empty
  // explanation is what made a refused row a dead end.
  return KNOWN_REASONS.includes(folded)
    ? t(`WASLO.TEMPLATES.REASON.${folded}`)
    : raw;
});

/** The delivery window on one line, when Meta told us and it is not its own default. */
const deliveryWindow = computed(() => {
  const described = describeTtl(props.template.messageSendTtlSeconds);
  if (!described) return '';
  return t(`WASLO.TEMPLATES.FACTS.WINDOW.${described.unit}`, {
    count: described.value,
  });
});

const editable = computed(() => canEdit(props.template));

/**
 * Parts of this template the form cannot hold — and therefore would DELETE, because Meta's edit
 * replaces every component with what the request carries. Named, so the sentence says "a picture"
 * rather than "this cannot be edited".
 */
const blockers = computed(() => editBlockers(props.template));

const blockedReason = computed(() => {
  const first = blockers.value[0];
  if (!first) return '';
  return t(`WASLO.TEMPLATES.EDIT_BLOCKED.${first.reason}`, {
    part: blockerPart(first, t),
  });
});
</script>

<template>
  <li
    class="flex flex-col gap-2 p-4 outline outline-1 -outline-offset-1 outline-n-weak rounded-xl bg-n-solid-1"
    :data-waslo-template="template.name"
  >
    <div class="flex items-start justify-between gap-3">
      <div class="flex flex-col min-w-0 gap-1">
        <span class="text-xs text-n-slate-10">
          {{ t('WASLO.TEMPLATES.ROW.NAME') }}
        </span>
        <!--
          The exact stored spelling. Chatwoot matches the name and the language exactly at send
          time, so an owner who copies a re-cased version of either builds a send that dies.
        -->
        <span
          class="min-w-0 font-mono text-sm break-all text-n-slate-12"
          dir="ltr"
          data-waslo-template-name
        >
          {{ template.name }}
        </span>
      </div>
      <!--
        Two chips, never one. The verdict and the rating are different facts and used to share a
        colour: approved-but-low rendered the same ruby as refused. See `TemplateQualityChip`.
      -->
      <div class="flex flex-wrap items-center gap-2">
        <TemplateStatusChip :status="template.status" />
        <TemplateQualityChip :quality="template.quality" />
      </div>
    </div>

    <p
      class="mb-0 overflow-y-auto text-sm leading-6 break-words whitespace-pre-wrap max-h-40 text-n-slate-11"
      dir="auto"
      data-waslo-template-body
    >
      {{ template.body }}
    </p>

    <p
      v-if="reason"
      class="mb-0 text-sm leading-6 max-w-[62ch] text-n-ruby-9"
      dir="auto"
      data-waslo-template-reason
    >
      {{ t('WASLO.TEMPLATES.ROW.REASON', { reason: reason }) }}
    </p>

    <div
      class="flex flex-wrap items-center min-w-0 gap-x-3 gap-y-1 text-xs text-n-slate-10"
    >
      <span>
        {{ t('WASLO.TEMPLATES.ROW.LANGUAGE') }}
        <bdi>{{ template.language }}</bdi>
      </span>
      <span aria-hidden="true">·</span>
      <span data-waslo-template-category>
        {{ t('WASLO.TEMPLATES.ROW.CATEGORY') }}
        <bdi>{{ category }}</bdi>
      </span>
      <span aria-hidden="true">·</span>
      <span data-waslo-template-blanks>
        {{
          blanks === 0
            ? t('WASLO.TEMPLATES.ROW.NO_BLANKS')
            : t('WASLO.TEMPLATES.ROW.BLANKS', { count: blanks })
        }}
      </span>
      <template v-if="deliveryWindow">
        <span aria-hidden="true">·</span>
        <span data-waslo-template-row-window>{{ deliveryWindow }}</span>
      </template>
    </div>

    <div class="flex flex-wrap items-center gap-2 mt-1">
      <!--
        TWO SEPARATE REASONS THE DOOR MAY BE SHUT, and they are shown differently on purpose.

        Meta's status rule — "only templates with an APPROVED, REJECTED, or PAUSED status can be
        edited" — means there is nothing to offer at all, so no button appears.

        A part this form cannot hold is different: the template IS editable, just not here. The
        button stays, DISABLED, with a sentence naming the part and pointing at WhatsApp Manager,
        which can edit it today. Hiding it would leave an owner hunting for a door that exists.
      -->
      <Button
        v-if="editable"
        :label="t('WASLO.TEMPLATES.ROW.EDIT')"
        icon="i-lucide-pencil"
        variant="faded"
        color="slate"
        size="sm"
        :disabled="blockers.length > 0"
        data-waslo-template-edit
        @click="emit('edit')"
      />
      <Button
        :label="
          open
            ? t('WASLO.TEMPLATES.ROW.HIDE_FACTS')
            : t('WASLO.TEMPLATES.ROW.SHOW_FACTS')
        "
        :icon="open ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        variant="ghost"
        color="slate"
        size="sm"
        :aria-expanded="open"
        data-waslo-template-expand
        @click="open = !open"
      />
    </div>

    <p
      v-if="editable && blockers.length"
      class="mb-0 text-xs leading-5 max-w-[62ch] text-n-slate-10"
      dir="auto"
      data-waslo-template-edit-blocked
    >
      {{ blockedReason }}
    </p>

    <TemplateFacts
      v-if="open"
      :template="template"
      :fields="fields"
      :manager-url="managerUrl"
      :busy="busy"
      :account-id="accountId"
      :inbox-id="inboxId"
      @unpause="emit('unpause')"
      @remove="emit('remove')"
    />
  </li>
</template>
